import type * as React from "react";

import {
  type ChatProvider,
  type ConnectionSummary,
  type EmailProvider,
  asDriveProvider,
} from "@ando/core";

import { ChatSyncFiltersSheet, DocSyncManager, EmailSyncFiltersSheet } from "@/features/sync";

/**
 * Mounts the three per-capability sync surfaces:
 *
 *   • ``EmailSyncFiltersSheet`` — slide-in right when the user picks a
 *     Gmail/Outlook account from the connected-accounts list. Carries the
 *     connection's email + provider.
 *   • ``ChatSyncFiltersSheet`` — same pattern with provider-conditional
 *     picker (Teams: teams + channels, Google Chat: spaces by type, Slack:
 *     chat-type toggles only).
 *   • ``DocSyncManager`` — two-stage doc surface: native picker (Google
 *     Picker / OneDrive tree / SharePoint multi-step / Notion DOM overlay)
 *     to add tracked folders + files, then a tracked-content list with
 *     selection.
 *
 * The route owns the three target slots and clears them on close — passing
 * them in (and the matching capability key) lets this component stay
 * stateless. Capability gating mirrors the inline route logic exactly:
 * a sheet only renders when its capability matches AND its target slot
 * is non-null.
 */
export interface SyncSheetsLayerProps {
  readonly capability: string;
  readonly emailSyncTarget: ConnectionSummary | null;
  readonly chatSyncTarget: ConnectionSummary | null;
  readonly docsSyncTarget: ConnectionSummary | null;
  readonly onCloseEmailSync: () => void;
  readonly onCloseChatSync: () => void;
  readonly onCloseDocsSync: () => void;
}

export const SyncSheetsLayer: React.FC<SyncSheetsLayerProps> = ({
  capability,
  emailSyncTarget,
  chatSyncTarget,
  docsSyncTarget,
  onCloseEmailSync,
  onCloseChatSync,
  onCloseDocsSync,
}) => {
  // The user-facing provider now rides ``connection.provider`` (BE surfaces it
  // for public capabilities); ``vendor`` is the enterprise fallback. The drive
  // provider is validated so the picker never routes on an unchecked cast
  // (the source of "Provider non supportato: undefined").
  const driveProvider =
    docsSyncTarget && capability === "drive"
      ? asDriveProvider(docsSyncTarget.provider ?? docsSyncTarget.vendor)
      : null;

  return (
    <>
      {emailSyncTarget &&
      (capability === "email" || capability === "aruba_pec" || capability === "infocert_pec") ? (
        <EmailSyncFiltersSheet
          open={emailSyncTarget !== null}
          onOpenChange={(o) => {
            if (!o) onCloseEmailSync();
          }}
          emailAddress={emailSyncTarget.connection_key ?? ""}
          // PEC → provider "aruba"/"infocert" (the /email-management/* slug the
          // BE routes to its IMAP path + host). Gmail/Outlook keep the
          // connection's own provider. (White-label strips ``provider`` for PEC,
          // so map by capability rather than trusting the vendor slug.)
          provider={
            capability === "aruba_pec"
              ? "aruba"
              : capability === "infocert_pec"
                ? "infocert"
                : ((emailSyncTarget.provider ?? emailSyncTarget.vendor) as EmailProvider)
          }
        />
      ) : null}

      {chatSyncTarget && capability === "messaging" ? (
        <ChatSyncFiltersSheet
          open={chatSyncTarget !== null}
          onOpenChange={(o) => {
            if (!o) onCloseChatSync();
          }}
          provider={(chatSyncTarget.provider ?? chatSyncTarget.vendor) as ChatProvider}
          connectionKey={chatSyncTarget.connection_key ?? undefined}
        />
      ) : null}

      {docsSyncTarget && capability === "drive" && driveProvider ? (
        <DocSyncManager
          open={docsSyncTarget !== null}
          onOpenChange={(o) => {
            if (!o) onCloseDocsSync();
          }}
          provider={driveProvider}
          connectionKey={docsSyncTarget.connection_key ?? undefined}
          accountLabel={docsSyncTarget.connection_key ?? undefined}
        />
      ) : null}
    </>
  );
};
SyncSheetsLayer.displayName = "SyncSheetsLayer";
